<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { showcaseCvRow } from '~/constants/showcaseCvs'

// Get the current page props/data
const { t } = useI18n()
const route = useRoute()
const user = useSupabaseUser()
const supabase = useSupabaseClient()
const { resolvePublicCvId } = usePublicCvId()

// The row id behind the URL, which may carry the CV's public slug instead
const cvId = ref<string>('')

// User data from CV
const userName = ref<string>('')
const userTitle = ref<string>('')
const userPhoto = ref<string>('')
const cvOwnerId = ref<string>('')
const isPublic = ref<boolean>(false)
const isLoading = ref<boolean>(true)
const isAdmin = ref<boolean>(false)
// Admin panel ref
const adminPanelRef = ref()

// Check if current user is the owner
const isOwner = computed(() => {
  return user.value?.id === cvOwnerId.value
})

// Check if current user can view (owner, admin, or public CV)
const canView = computed(() => {
  return isOwner.value || isAdmin.value || isPublic.value
})

// Toggle public visibility - just send signal to page component
const handleTogglePublic = () => {
  // Send signal to page component to execute togglePublicStatus
  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('toggle-public-status-request'))
  }
}

// Change web template
const handleChangeTemplate = async (template: string) => {
  try {
    if (!cvId.value) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('cv_requests')
      .update({ business_web_template: template })
      .eq('id', cvId.value)

    if (!error) {
      // Emit event to update template in page component
      if (import.meta.client) {
        window.dispatchEvent(new CustomEvent('template-changed', { detail: { template } }))
      }
    } else {
      console.error('Error updating template:', error)
    }
  } catch (e) {
    console.error('Error changing template:', e)
  }
}

// Change CV template
const handleChangeCvTemplate = async (template: string) => {
  try {
    if (!cvId.value) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('cv_requests')
      .update({ template: template })
      .eq('id', cvId.value)

    if (!error) {
      // Emit event to update CV template in page component
      if (import.meta.client) {
        window.dispatchEvent(new CustomEvent('cv-template-changed', { detail: { template } }))
      }
    } else {
      console.error('Error updating CV template:', error)
    }
  } catch (e) {
    console.error('Error changing CV template:', e)
  }
}

// Download contact function
const downloadContact = () => {
  if (!userName.value) return

  // Create vCard format
  const vcard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${userName.value}`,
    userTitle.value ? `TITLE:${userTitle.value}` : '',
    'END:VCARD'
  ].filter(line => line && line !== '').join('\n')

  // Create download
  const blob = new Blob([vcard], { type: 'text/vcard' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${userName.value.replace(/\s+/g, '_')}.vcf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Export to PDF function
const _exportToPDF = () => {
  // Send signal to page component to execute PDF export
  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('export-pdf-request'))
  }
}

// Load CV data
onMounted(async () => {
  isLoading.value = false // Layout will wait for page to send data

  // Always load CV data first (needed for public access)
  cvId.value = await resolvePublicCvId() ?? ''
  const { data: row } = cvId.value
    ? await (supabase as any)
      .from('cv_requests')
      .select('parsed_data, user_id, is_public, business_web_template')
      .eq('id', cvId.value)
      .single()
    : { data: null }
  // A gallery example whose row has not been created yet: the demo sample
  const data = row ?? showcaseCvRow(cvId.value)
  console.log('Layout: Data', data)
  if (data) {
    const parsedData = data.parsed_data as { name?: string; title?: string; photoUrl?: string } | null
    userName.value = parsedData?.name || ''
    userTitle.value = parsedData?.title || ''
    userPhoto.value = parsedData?.photoUrl || ''
    cvOwnerId.value = data.user_id || ''
    isPublic.value = data.is_public || false
  }

  // Check if current user is admin (only if user is logged in)
  if (user.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: userRoles } = await (supabase as any)
      .from('v_user_roles_secure')
      .select('role_name')
      .eq('user_id', user.value.id)
      .eq('is_role_active', true)
      .limit(1)
    console.log('Layout: User roles', userRoles)
    isAdmin.value = (userRoles && userRoles.length > 0 && userRoles[0]?.role_name === 'Admin') || false
    console.log('Layout: User is admin?', isAdmin.value)
  }

  // Listen for public status changes from page component
  if (import.meta.client) {
    window.addEventListener('public-status-changed-from-page', (event: Event) => {
      const customEvent = event as CustomEvent<{ isPublic: boolean }>
      isPublic.value = customEvent.detail.isPublic
      // Update admin panel state
      if (adminPanelRef.value) {
        adminPanelRef.value.setPublicStatus(customEvent.detail.isPublic)
        adminPanelRef.value.setTogglingState(false)
      }
    })

    // Listen for toggle completion to reset loading state
    window.addEventListener('toggle-public-status-complete', () => {
      if (adminPanelRef.value) {
        adminPanelRef.value.setTogglingState(false)
      }
    })
  }
})
</script>

<template>
  <div class="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900" :dir="direction">
    <!-- Admin Navigation Bar (Only for Owner) -->
    <CommonAdminPanel
v-if="isOwner" ref="adminPanelRef" :cv-id="cvId"
      :show-template-selector="false" :show-cv-template-selector="true" :show-public-toggle="true"
      :show-edit-button="true" :show-my-cvs-button="true" @toggle-public="handleTogglePublic"
      @change-template="handleChangeTemplate" @change-cv-template="handleChangeCvTemplate" />

    <!-- Modern Header with User Info -->
    <nav
      class="fixed z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur-lg dark:border-gray-800 dark:bg-gray-900/95"
      :class="isOwner ? 'top-14' : 'top-0'">
      <div class="mx-auto max-w-screen-xl px-4">
        <!-- Public CV or Can View (Owner/Admin) - Simple Navigation -->
        <div v-if="canView" class="flex h-14 items-center justify-between sm:h-16">
          <!-- Logo/Name -->
          <div class="flex items-center space-x-2 sm:space-x-3">
            <div class="flex size-8 items-center justify-center rounded-lg bg-indigo-600 sm:size-10 sm:rounded-xl">
              <img v-if="userPhoto" :src="userPhoto" :alt="userName" class="size-full rounded-xl object-cover">
              <div v-else class="text-sm font-bold text-white sm:text-lg">{{ userName?.charAt(0) }}</div>
            </div>
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <div
                  class="max-w-[120px] truncate text-sm font-semibold text-gray-900 sm:max-w-none sm:text-base dark:text-white">
                  {{ userName }}</div>
                <div
v-if="isAdmin && !isOwner"
                  class="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-200">
                  {{ t('publicPage.adminView') }}
                </div>
              </div>
              <div v-if="userTitle" class="text-xs text-gray-600 dark:text-gray-400">{{ userTitle }}</div>
            </div>
            <div class="mx-2 hidden h-6 w-px bg-gray-300 sm:block dark:bg-gray-600" />

            <!-- InMyResume Logo (smaller) -->
            <div class="hidden items-center space-x-1 opacity-70 transition-opacity hover:opacity-100 sm:flex">
              <LocaleLink to="/" class="flex items-center">
                <NuxtImg src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-7 w-auto dark:hidden" />
                <NuxtImg
src="/logo_white.svg" alt="InMyResume" width="264" height="56"
                  class="hidden h-7 w-auto dark:block" />
              </LocaleLink>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2">
            <button
              class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-indigo-700"
              @click="downloadContact">
              {{ t('cv.edit.saveContact') }}
            </button>
          </div>
        </div>

        <!-- Private CV - Simple Navigation with Logo and Auth Buttons -->
        <div v-else class="flex h-14 items-center justify-between sm:h-16">
          <!-- InMyResume Logo -->
          <LocaleLink to="/" class="flex items-center">
            <NuxtImg src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-9 w-auto dark:hidden" />
            <NuxtImg
src="/logo_white.svg" alt="InMyResume" width="264" height="56"
              class="hidden h-9 w-auto dark:block" />
          </LocaleLink>

          <!-- Auth Buttons -->
          <div class="flex items-center space-x-3">
            <LocaleLink
to="/login"
              class="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
              {{ t('publicPage.signIn') }}
            </LocaleLink>
            <LocaleLink
to="/registration"
              class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
              {{ t('publicPage.signUp') }}
            </LocaleLink>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main content area with proper spacing -->
    <main class="flex flex-1" :class="isOwner ? 'mt-14' : ''">
      <div class="w-full">

        <!-- Show content if public OR if user can view (owner/admin) -->
        <div v-if="(canView) && !isLoading">
          <slot />
        </div>
        <!-- Show loading spinner if loading -->
        <div v-else-if="isLoading" class="flex min-h-screen items-center justify-center px-4">
          <LoadingSpinner />
        </div>

        <!-- Show "Not Public" message if not public and user cannot view -->
        <div v-else class="flex min-h-screen items-center justify-center px-4">
          <div class="max-w-md text-center">
            <div class="mb-6 flex justify-center">
              <div class="rounded-full bg-gray-100 p-6 dark:bg-gray-800">
                <svg
class="size-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <h1 class="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
              {{ t('publicPage.private.title') }}
            </h1>

            <!-- Message for owner -->
            <p v-if="isOwner" class="mb-8 text-gray-600 dark:text-gray-400">
              {{ t('publicPage.private.owner') }}
            </p>
            <!-- Message for non-owner -->
            <p v-else class="mb-8 text-gray-600 dark:text-gray-400">
              {{ t('publicPage.private.visitor') }}
            </p>

            <!-- Buttons for owner -->
            <div v-if="isOwner" class="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                class="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700"
                @click="handleTogglePublic">
                {{ t('publicPage.makePublic') }}
              </button>
              <LocaleLink
to="/cv"
                class="rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
                {{ t('publicPage.goToMyCvs') }}
              </LocaleLink>
            </div>

            <!-- Buttons for non-owner -->
            <div v-else class="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <LocaleLink
to="/login"
                class="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700">
                {{ t('publicPage.signIn') }}
              </LocaleLink>
              <LocaleLink
to="/"
                class="rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
                {{ t('publicPage.goHome') }}
              </LocaleLink>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Minimal Footer -->
    <footer class="border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div class="mx-auto max-w-screen-xl px-4 py-6">
        <div class="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
          <div class="text-sm text-gray-600 dark:text-gray-400">
            © {{ new Date().getFullYear() }} InMyResume. {{ t('footer.rights') }}.
          </div>
          <div class="flex items-center space-x-6">
            <LocaleLink
to="/privacy"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.privacy') }}
            </LocaleLink>
            <LocaleLink
to="/terms"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.terms') }}
            </LocaleLink>
            <a
href="mailto:info@inmyresume.com"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.contact') }}
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style>
@media print {
  body {
    background: white !important;
  }

  nav,
  footer,
  .print\:hidden {
    display: none !important;
  }
}
</style>
